import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TourVoteInsights from "@/components/TourVoteInsights";
import { getUser, logout } from "@netlify/identity";
import { Camera, Images, Loader2, LogOut, MonitorUp, QrCode, Sparkles } from "lucide-react";

const ADMIN_EMAIL = "info@geauxfotos.com";

const adminTools = [
  ["GeauxFrame", "Event galleries, Review Deck, face claims, Develop, printing and delivery.", "https://fotos.geauxfotos.com/admin", Images, true],
  ["Creative Cloud Studio", "Private Higgsfield image/video generation and production history.", "/admin/cloud-studio", Sparkles, false],
  ["Rook Projection Studio", "Live projection mapping, projector outputs and phone controls.", "/rook/", MonitorUp, true],
  ["QR Drop Cards", "Open the event QR/drop-card tools.", "/cards", QrCode, false],
  ["Public Gallery", "Review the guest-facing gallery experience.", "/gallery", Camera, false],
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let active = true;
    getUser().then(async (user) => {
      if (!active) return;
      if (!user || user.email?.trim().toLowerCase() !== ADMIN_EMAIL) {
        if (user) await logout();
        nav("/admin/login", { replace: true });
        return;
      }
      setAuthorized(true);
    }).catch(() => nav("/admin/login", { replace: true }));
    return () => { active = false; };
  }, [nav]);

  const signOut = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  if (!authorized) {
    return <div className="min-h-screen bg-[#090909] text-[#C8FF00] grid place-items-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#090909] text-[#F5F0E8]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#090909]/90 px-5 py-4 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
          <div>
            <p className="font-accent text-[10px] tracking-[0.28em] text-[#C8FF00]">FOTOS ON THE GEAUX · PRIVATE</p>
            <h1 className="font-display text-3xl tracking-tight">Admin Hub</h1>
          </div>
          <button type="button" onClick={signOut} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-[#E32636]">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        <section className="rounded-3xl border border-white/10 bg-[#0d0d0d] p-7 md:p-10">
          <p className="font-accent text-xs tracking-[.22em] text-[#C8FF00]">ONE LOGIN · PRODUCTION TOOLS</p>
          <h2 className="mt-3 font-display text-4xl tracking-tight md:text-6xl">Run the night from one place.</h2>
          <p className="mt-4 max-w-2xl text-white/55">The retired ticket-dashboard token flow is no longer part of the live admin. This hub uses the same Netlify Identity session as Cloud Studio and routes each production system to its authoritative admin surface.</p>
        </section>
        <section className="mt-6 grid gap-4 md:grid-cols-2">
          {adminTools.map(([title, description, href, Icon, external]) => {
            const classes = "group rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 transition hover:-translate-y-0.5 hover:border-[#C8FF00]/55";
            const body = <>
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[#C8FF00] text-black"><Icon size={19} /></span>
              <h3 className="mt-5 font-display text-2xl">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-white/50">{description}</p>
              <span className="mt-5 inline-block font-accent text-[10px] tracking-[.18em] text-[#C8FF00]">OPEN →</span>
            </>;
            return external
              ? <a key={title} href={href} className={classes}>{body}</a>
              : <Link key={title} to={href} className={classes}>{body}</Link>;
          })}
        </section>
        <TourVoteInsights />
      </main>
    </div>
  );
}
